import React, { Component, Suspense } from "react";
import { Spin } from "antd";
import { isIgnoredBrowserNoise } from "../../utils/sentryNoise";
import { SchoolAssessmentErrorFallback } from "./SchoolAssessmentErrorFallback";
import { getAssessmentTheme } from "../../hooks/useAssessmentTheme";

interface ShellProps {
    children: React.ReactNode;
}

interface ShellState {
    hasError: boolean;
    error: unknown;
}

class SchoolAssessmentErrorBoundary extends Component<ShellProps, ShellState> {
    state: ShellState = { hasError: false, error: null };

    static getDerivedStateFromError(error: unknown): ShellState {
        if (isIgnoredBrowserNoise(error)) {
            return { hasError: false, error: null };
        }
        return { hasError: true, error };
    }

    componentDidCatch(error: unknown) {
        if (isIgnoredBrowserNoise(error)) return;
        console.error("School assessment render error:", error);
    }

    handleRetry = () => {
        this.setState({ hasError: false, error: null });
    };

    render() {
        if (this.state.hasError) {
            return (
                <SchoolAssessmentErrorFallback
                    title="Something went wrong"
                    error={this.state.error}
                    onRetry={this.handleRetry}
                />
            );
        }
        return this.props.children;
    }
}

const LoadingFallback = () => {
    const theme = getAssessmentTheme(null);
    return (
        <div className={`school-assessment-entry ${theme.cssClass}`}>
            <div className="student-card" style={{ textAlign: "center", padding: 40 }}>
                <Spin size="large" />
                <div style={{ marginTop: 12, color: "#888780" }}>Loading assessment...</div>
            </div>
        </div>
    );
};

/** Isolates public school entry pages from the global error page and adds retry UX. */
export const SchoolAssessmentRouteShell: React.FC<ShellProps> = ({ children }) => (
    <SchoolAssessmentErrorBoundary>
        <Suspense fallback={<LoadingFallback />}>{children}</Suspense>
    </SchoolAssessmentErrorBoundary>
);

export default SchoolAssessmentRouteShell;
